import React, { useState } from 'react';
import {Button, Box, Checkbox, Dialog, DialogContent, TextField, Typography,
  GridLegacy as Grid, Select, MenuItem, FormControl, InputLabel} from '@mui/material';
import {registerUser} from "helpers/userManagement.js";
import AuthContext from 'contexts/AuthContext';

export default function RegisterUser() {
  const {user} = React.useContext(AuthContext);

  // Helper function to convert ISO date to YYYY-MM-DD format
  const formatDateForInput = (isoDate) => {
    if (!isoDate) return '';
    try {
      const date = new Date(isoDate);
      return date.toISOString().split('T')[0];
    } catch (error) {
      return '';
    }
  };

  // Helper function to convert YYYY-MM-DD format back to ISO string
  const formatDateForSubmit = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString + 'T00:00:00.000Z');
      return date.toISOString();
    } catch (error) {
      return '';
    }
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    email: user?.email || '',
    name: user?.name || '',
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    gender: user?.gender || '',
    dateOfBirth: formatDateForInput(user?.dateOfBirth) || '',
    phoneNumber: user?.phoneNumber || '',
    plotNumber: '',
    isSelfOccupied: true,
    tenantName: '',
    tenantPhone: '',
  });

  const handleInputChange = (field, value) => {
    setFormData(prevState => ({ ...prevState, [field]: value }));
  };

  const handleSubmit = async () => {
    const requiredFields = ['name', 'gender', 'dateOfBirth', 'plotNumber'];
    const missingFields = requiredFields.filter(field => !formData[field]);

    if (missingFields.length > 0) {
      alert(`Please fill in required fields: ${missingFields.join(', ')}`);
      return;
    }

    try {
      // Convert dateOfBirth back to ISO format before submitting
      const submitData = {
        ...formData,
        dateOfBirth: formatDateForSubmit(formData.dateOfBirth)
      };

      await registerUser(submitData);
      alert('User registered successfully!');
      setIsModalOpen(false);
      // Reset form (keeping email from context)
      setFormData({
        email: user?.email || '',
        name: '',
        firstName: '',
        lastName: '',
        gender: '',
        dateOfBirth: '',
        phoneNumber: '',
        plotNumber: '',
        isSelfOccupied: true,
        tenantName: '',
        tenantPhone: '',
        moveInDate: ''
      });
    } catch (error) {
      console.error('Error registering user:', error);
      alert('Failed to register user. Please try again.');
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      {/* Fixed Register Button */}
      <Box
        sx={{
          position: 'fixed',
          bottom: { xs: 20, sm: 60 },
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 999
        }}
      >
        <Button color="error"
          variant="contained"
          size="large"
          onClick={() => setIsModalOpen(true)}
          sx={{
            px: { xs: 2, sm: 4 },
            py: { xs: 1, sm: 1.5 },
            fontSize: { xs: '14px', sm: '16px' }
          }}
        >
          Register Now
        </Button>
      </Box>

      {/* Registration Modal */}
      <Dialog open={isModalOpen} onClose={closeModal} fullWidth maxWidth="md"
        fullScreen={window.innerWidth < 600}
        sx={{'& .MuiDialog-paper': {margin: { xs: 1, sm: 2 }, zIndex: 1000}}}
      >
        <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
          <Box sx={{ width: '100%' }}>
            <Typography
              variant="h6"
              component="h2"
              textAlign="center"
              color="text.secondary"
              sx={{ mb: 3 }}
            >
            </Typography>

            <Grid container spacing={2}>
              {/* Plot Number - First input */}
              <Grid item xs={12} >
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Enter plot number"
                  label="Plot Number *"
                  type="number"
                  value={formData.plotNumber}
                  onChange={(e) => handleInputChange('plotNumber', e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <Box component="span" sx={{ mr: 1, color: 'primary.main' }}>
                        🏠
                      </Box>
                    )
                  }}
                />
              </Grid>

              {/* Name - Full width */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Enter full name"
                  label="Name *"
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <Box component="span" sx={{ mr: 1, color: 'primary.main' }}>
                        👤
                      </Box>
                    )
                  }}
                />
              </Grid>

              {/* First Name and Last Name */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Enter first name"
                  label="First Name"
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <Box component="span" sx={{ mr: 1, color: 'primary.main' }}>
                        👤
                      </Box>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Enter last name"
                  label="Last Name"
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <Box component="span" sx={{ mr: 1, color: 'primary.main' }}>
                        👤
                      </Box>
                    )
                  }}
                />
              </Grid>

              {/* Gender and Date of Birth */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Gender *</InputLabel>
                  <Select
                    value={formData.gender}
                    onChange={(e) => handleInputChange('gender', e.target.value)}
                    label="Gender *"
                    startAdornment={
                      <Box component="span" sx={{ mr: 1, color: 'primary.main' }}>
                        ⚧
                      </Box>
                    }
                  >
                    <MenuItem value="">Select Gender</MenuItem>
                    <MenuItem value="Male">Male</MenuItem>
                    <MenuItem value="Female">Female</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Enter date of birth"
                  label="Date of Birth *"
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <Box component="span" sx={{ mr: 1, color: 'primary.main' }}>
                        🎂
                      </Box>
                    )
                  }}
                />
              </Grid>

              {/* Phone Number */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Enter phone number"
                  label="Phone Number"
                  type="tel"
                  value={formData.phoneNumber}
                  onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <Box component="span" sx={{ mr: 1, color: 'primary.main' }}>
                        📞
                      </Box>
                    )
                  }}
                />
              </Grid>

              {/* Is Self Occupied Checkbox */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Checkbox
                    checked={formData.isSelfOccupied}
                    onChange={(e) => handleInputChange('isSelfOccupied', e.target.checked)}
                    color="primary"
                  />
                  <Typography variant="body1" color="text.secondary">
                    Is Self Occupied *
                  </Typography>
                </Box>
              </Grid>

              {/* Tenant Details - Only if not self occupied */}
              {!formData.isSelfOccupied && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth
                      variant="outlined"
                      placeholder="Enter tenant name"
                      label="Tenant Name *"
                      type="text"
                      value={formData.tenantName}
                      onChange={(e) => handleInputChange('tenantName', e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <Box component="span" sx={{ mr: 1, color: 'primary.main' }}>
                            👥
                          </Box>
                        )
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      placeholder="Enter tenant phone"
                      label="Tenant Phone"
                      type="tel"
                      value={formData.tenantPhone}
                      onChange={(e) => handleInputChange('tenantPhone', e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <Box component="span" sx={{ mr: 1, color: 'primary.main' }}>
                            📞
                          </Box>
                        )
                      }}
                    />
                  </Grid>
                </>
              )}

              {/* Action Buttons */}
              <Grid item xs={12}>
                <Box sx={{display: 'flex', justifyContent: 'space-between', gap: 2,
                  flexDirection: {xs: 'column', sm: 'row'} }} >
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={closeModal}
                    size="large"
                    sx={{ minWidth: { xs: '100%', sm: '120px' } }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={handleSubmit}
                    size="large"
                    sx={{ minWidth: { xs: '100%', sm: '120px' } }}
                  >
                    Submit
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}
